import React, { createContext, useContext, useMemo, useState, useEffect } from "react";

export const DICT = {
  en: {
    tagline: "The public index of brand transparency",
    nav_brands: "Brands",
    nav_sectors: "Sectors",
    nav_dpp: "About DPP",
    nav_methodology: "Methodology",
    nav_about: "About",
    nav_for_brands: "For brands",
    hero_title: "The public index of brand transparency.",
    hero_sub: "BrandPassport documents what brands publish about their products and how ready they are for the EU Digital Product Passport. No scores. No rankings. Just facts.",
    search_placeholder: "Search a brand by name…",
    section_sectors: "Browse by sector",
    section_recent: "Recently updated profiles",
    chronological_note: "Chronological · not ranked",
    empty_kicker: "Index under construction",
    empty_title: "The index is being built — brand by brand, source by source.",
    empty_body: "Profiles are published only after a human editor has reviewed the extracted evidence against the seven transparency categories. Nothing appears here until someone at BrandPassport has read the source pages and confirmed them. Once the first profiles are approved, they will show up in this section within a minute.",
    empty_cta_methodology: "Read our methodology",
    empty_cta_for_brands: "Are you a brand? Learn how to contribute",
    section_dpp_title: "What is the Digital Product Passport?",
    section_dpp_body: "The Digital Product Passport (DPP) is an EU initiative under the Ecodesign for Sustainable Products Regulation (Regulation EU 2024/1781, ESPR). It will require most products placed on the EU market to carry a digital record of key information — materials, origin, repairability, environmental footprint and end-of-life — accessible via a data carrier attached to the product. Textiles are among the first product groups covered, with rules expected to enter into force in 2027.",
    section_dpp_clarify: "BrandPassport is an index — we do not issue Digital Product Passports.",
    dpp_read_more: "Read the full DPP explainer →",
    partners_title: "In collaboration with",
    footer_disclaimer: "BrandPassport is an index: we do not issue Digital Product Passports. We summarise publicly available information. Absence of information means we didn't find it, not that it doesn't exist. This is not a compliance assessment.",
    checklist_categories: {
      PRODUCT_IDENTITY: "Product identity",
      MATERIALS: "Materials",
      ORIGIN: "Where it's made",
      REPAIR_CARE: "Repair & care",
      END_OF_LIFE: "End of life",
      FOOTPRINT: "Footprint",
      CERTIFICATIONS: "Certifications",
    },
    status: { FOUND: "Found", PARTIAL: "Partial", NOT_FOUND: "Not found", NOT_ASSESSABLE: "Not assessable" },
    not_found_line: (date) => `Not found in public sources (checked ${date})`,
    last_checked: "Last checked",
    view_source: "View source",
    evidence: "Evidence excerpt",
    checked_on: "Checked on",
    dpp_readiness: "DPP readiness note",
    sources_title: "Sources",
    documents_title: "Documents analyzed by AI",
    documents_empty: "No PDF documents have been analyzed yet.",
    documents_intro: "PDF reports and policy documents parsed by the pipeline and used as evidence for this profile.",
    cite_button: "Cite",
    cite_copied: "Citation copied to clipboard",
    cite_failed: "Copy failed — select and copy manually",
    access_restricted_banner_title: "Access-restricted profile",
    access_restricted_banner_body: "This brand's website could not be accessed by our automated review. All seven categories are shown as Not assessable — no assessment of published information is possible.",
    access_restricted_attempted_title: "URLs attempted",
    access_restricted_robots_label: "Blocked by robots.txt",
    access_restricted_cdn_label: "Blocked by CDN / edge protection",
    access_restricted_other_label: "Blocked (other)",
    notify_when_readable_intro: "Get notified once (and only once) when this brand's sources become readable.",
    notify_when_readable_placeholder: "your@email.com",
    notify_when_readable_cta: "Notify me",
    notify_when_readable_submitting: "Sending…",
    notify_when_readable_success: "Check your inbox to confirm — no email will be sent until you click the link.",
    notify_when_readable_already: "You are already subscribed for this brand.",
    notify_when_readable_error: "Something went wrong. Please try again later.",
    notify_when_readable_invalid_email: "Please enter a valid email address.",
    notify_confirm_title: "Alert confirmed",
    notify_confirm_body: "You will receive one email when this brand's sources become readable.",
    notify_confirm_invalid: "This confirmation link is invalid or already used.",
    notify_unsub_title: "Unsubscribed",
    notify_unsub_body: "You will not receive further notifications for this brand.",
    notify_unsub_invalid: "This unsubscribe link is invalid or already used.",
    claim_cta_title: "Is this your brand?",
    claim_cta_body: "Allowlist our crawler and request a re-review.",
    claim_cta_ua_prefix: "User-Agent: ",
    claim_cta_button: "Claim & request re-run",
    changelog_title: "Changelog",
    claimed_badge: "Claimed profile",
    follow_cta: "Follow by email",
    compare_cta: "Compare",
    report_cta: "Report an error",
    share_cta: "Share",
    follow_placeholder: "you@example.com",
    follow_submit: "Send confirmation email",
    follow_pending: "Check your inbox to confirm your subscription.",
    report_title: "Report an error",
    report_intro: "Spotted something factually wrong? Tell us. We commit to responding within 10 business days.",
    report_field_item: "Which checklist item is affected?",
    report_field_desc: "What's wrong?",
    report_field_link: "Optional supporting link",
    report_field_email: "Your email (optional)",
    report_submit: "Submit report",
    report_confirm: "Thank you. We commit to responding within 10 business days.",
    compare_title: "Compare brands",
    compare_intro: "Pick 2 or 3 brands. Their checklists are aligned side by side. No totals, no winner.",
    compare_pick: "Add a brand to compare",
    sector_filter_factories: "Discloses factories",
    sector_filter_repair: "Repair info available",
    sector_filter_certs: "Verifiable certifications",
    sort_alpha: "Alphabetical",
    sort_recent: "Recently updated",
    sector_count: (n, s) => `Of ${n} brands indexed in ${s}, this page lists all published profiles.`,
    sector_materials_count: (n) => `${n} publish full material composition.`,
    publishes_of_seven: (n) => `Publishes ${n} of 7 categories`,
    no_results: "No brands match those filters.",
    not_in_index_title: "This brand is not in the index yet",
    not_in_index_body: "BrandPassport indexes brands over time. You can request that a brand be added.",
    request_brand_submit: "Request this brand",
    langs: { en: "EN", it: "IT" },
  },
  it: {
    tagline: "L'indice pubblico della trasparenza dei brand",
    nav_brands: "Brand",
    nav_sectors: "Settori",
    nav_dpp: "Cos'è il DPP",
    nav_methodology: "Metodologia",
    nav_about: "Chi siamo",
    nav_for_brands: "Per i brand",
    hero_title: "L'indice pubblico della trasparenza dei brand.",
    hero_sub: "BrandPassport documenta ciò che i brand pubblicano sui propri prodotti e quanto sono pronti per il Passaporto Digitale di Prodotto dell'UE. Nessun voto. Nessuna classifica. Solo fatti.",
    search_placeholder: "Cerca un brand per nome…",
    section_sectors: "Sfoglia per settore",
    section_recent: "Profili aggiornati di recente",
    chronological_note: "Cronologico · non classificato",
    empty_kicker: "Indice in costruzione",
    empty_title: "L'indice si sta costruendo — marchio dopo marchio, fonte dopo fonte.",
    empty_body: "I profili vengono pubblicati solo dopo che un redattore umano ha rivisto le evidenze estratte rispetto alle sette categorie di trasparenza. Nulla appare qui finché una persona di BrandPassport non ha letto le pagine sorgente e le ha confermate. Non appena i primi profili verranno approvati, compariranno in questa sezione entro un minuto.",
    empty_cta_methodology: "Leggi la metodologia",
    empty_cta_for_brands: "Sei un marchio? Scopri come contribuire",
    section_dpp_title: "Cos'è il Passaporto Digitale di Prodotto?",
    section_dpp_body: "Il Passaporto Digitale di Prodotto (DPP) è un'iniziativa dell'UE prevista dal Regolamento Ecodesign per Prodotti Sostenibili (Regolamento UE 2024/1781, ESPR). Richiederà alla maggior parte dei prodotti immessi sul mercato UE di avere una scheda digitale con informazioni chiave — materiali, origine, riparabilità, impronta ambientale e fine vita — accessibile tramite un supporto dati apposto sul prodotto. I tessili sono tra le prime categorie coperte, con norme attese in vigore nel 2027.",
    section_dpp_clarify: "BrandPassport è un indice — non emettiamo Passaporti Digitali di Prodotto.",
    dpp_read_more: "Leggi la spiegazione completa sul DPP →",
    partners_title: "In collaborazione con",
    footer_disclaimer: "BrandPassport è un indice: non emettiamo Passaporti Digitali di Prodotto. Riassumiamo informazioni pubblicamente disponibili. L'assenza di informazioni significa che non le abbiamo trovate, non che non esistano. Questa non è una valutazione di conformità.",
    checklist_categories: {
      PRODUCT_IDENTITY: "Identità del prodotto",
      MATERIALS: "Materiali",
      ORIGIN: "Dove è prodotto",
      REPAIR_CARE: "Riparazione e cura",
      END_OF_LIFE: "Fine vita",
      FOOTPRINT: "Impronta ambientale",
      CERTIFICATIONS: "Certificazioni",
    },
    status: { FOUND: "Trovato", PARTIAL: "Parziale", NOT_FOUND: "Non trovato", NOT_ASSESSABLE: "Non valutabile" },
    not_found_line: (date) => `Non trovato in fonti pubbliche (verificato il ${date})`,
    last_checked: "Ultimo controllo",
    view_source: "Vedi fonte",
    evidence: "Estratto della fonte",
    checked_on: "Verificato il",
    dpp_readiness: "Nota di prontezza DPP",
    sources_title: "Fonti",
    documents_title: "Documenti analizzati dall'IA",
    documents_empty: "Nessun documento PDF analizzato al momento.",
    documents_intro: "Report PDF e documenti di policy elaborati dalla pipeline e utilizzati come evidenza per questo profilo.",
    cite_button: "Cita",
    cite_copied: "Citazione copiata negli appunti",
    cite_failed: "Copia non riuscita — seleziona e copia manualmente",
    access_restricted_banner_title: "Profilo con accesso limitato",
    access_restricted_banner_body: "Il sito web di questo marchio non è stato accessibile alla nostra revisione automatica. Tutte e sette le categorie sono mostrate come Non valutabili — nessuna valutazione delle informazioni pubblicate è possibile.",
    access_restricted_attempted_title: "URL tentati",
    access_restricted_robots_label: "Bloccato da robots.txt",
    access_restricted_cdn_label: "Bloccato da CDN / protezione edge",
    access_restricted_other_label: "Bloccato (altro)",
    notify_when_readable_intro: "Ricevi una sola email quando le fonti di questo marchio diventeranno leggibili.",
    notify_when_readable_placeholder: "tua@email.com",
    notify_when_readable_cta: "Avvisami",
    notify_when_readable_submitting: "Invio…",
    notify_when_readable_success: "Controlla la tua casella per confermare — non riceverai email finché non clicchi il link.",
    notify_when_readable_already: "Sei già iscritto per questo marchio.",
    notify_when_readable_error: "Qualcosa è andato storto. Riprova più tardi.",
    notify_when_readable_invalid_email: "Inserisci un indirizzo email valido.",
    notify_confirm_title: "Avviso confermato",
    notify_confirm_body: "Riceverai un'email quando le fonti di questo marchio diventeranno leggibili.",
    notify_confirm_invalid: "Questo link di conferma non è valido o è già stato usato.",
    notify_unsub_title: "Iscrizione annullata",
    notify_unsub_body: "Non riceverai più notifiche per questo marchio.",
    notify_unsub_invalid: "Questo link di annullamento non è valido o è già stato usato.",
    claim_cta_title: "È il tuo marchio?",
    claim_cta_body: "Autorizza il nostro crawler e richiedi una nuova revisione.",
    claim_cta_ua_prefix: "User-Agent: ",
    claim_cta_button: "Rivendica e richiedi ri-esecuzione",
    changelog_title: "Registro modifiche",
    claimed_badge: "Profilo rivendicato",
    follow_cta: "Segui via email",
    compare_cta: "Confronta",
    report_cta: "Segnala un errore",
    share_cta: "Condividi",
    follow_placeholder: "tu@esempio.com",
    follow_submit: "Invia email di conferma",
    follow_pending: "Controlla la tua casella per confermare l'iscrizione.",
    report_title: "Segnala un errore",
    report_intro: "Hai notato qualcosa di fattualmente errato? Segnalacelo. Ci impegniamo a rispondere entro 10 giorni lavorativi.",
    report_field_item: "Quale voce della checklist è interessata?",
    report_field_desc: "Cosa non va?",
    report_field_link: "Link di supporto (facoltativo)",
    report_field_email: "La tua email (facoltativa)",
    report_submit: "Invia segnalazione",
    report_confirm: "Grazie. Ci impegniamo a rispondere entro 10 giorni lavorativi.",
    compare_title: "Confronta brand",
    compare_intro: "Seleziona 2 o 3 brand. Le loro checklist saranno allineate una accanto all'altra. Nessun totale, nessun vincitore.",
    compare_pick: "Aggiungi un brand da confrontare",
    sector_filter_factories: "Fabbriche dichiarate",
    sector_filter_repair: "Info di riparazione",
    sector_filter_certs: "Certificazioni verificabili",
    sort_alpha: "Alfabetico",
    sort_recent: "Aggiornati di recente",
    sector_count: (n, s) => `Su ${n} brand indicizzati in ${s}, questa pagina elenca tutti i profili pubblicati.`,
    sector_materials_count: (n) => `${n} pubblicano la composizione completa dei materiali.`,
    publishes_of_seven: (n) => `Pubblica ${n} categorie su 7`,
    no_results: "Nessun brand corrisponde ai filtri.",
    not_in_index_title: "Questo brand non è ancora nell'indice",
    not_in_index_body: "BrandPassport indicizza i brand nel tempo. Puoi richiedere l'aggiunta di un brand.",
    request_brand_submit: "Richiedi questo brand",
    langs: { en: "EN", it: "IT" },
  },
};

const I18nCtx = createContext({ locale: "en", t: DICT.en, setLocale: () => {} });

export const I18nProvider = ({ children }) => {
  const [locale, setLocale] = useState(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem("bp_locale");
    if (stored === "it" || stored === "en") return stored;
    return (navigator.language || "en").startsWith("it") ? "it" : "en";
  });
  useEffect(() => {
    try { window.localStorage.setItem("bp_locale", locale); } catch (e) { console.debug("localStorage unavailable", e); }
    document.documentElement.setAttribute("lang", locale);
  }, [locale]);
  const value = useMemo(() => ({ locale, t: DICT[locale], setLocale }), [locale]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
};

export const useI18n = () => useContext(I18nCtx);
export const pickLocalized = (obj, locale) => (obj && (obj[locale] || obj.en || obj.it)) || "";
